// @flow
import * as React from 'react';
import merge from 'lodash/merge';
import { withRouter } from 'react-router-dom';

import Downshift from 'downshift';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import type { Node } from 'react';

import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Search from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';

import env from '../../utils/env';
import styles from './styles';

import withSnackbar from '../../HOCs/withSnackbar';
import withDialogRouter from '../../HOCs/withDialogRouter';
import SSR from '../../HOCs/serverRender';
import { REGEX, TOP_TLDS, MAX_SEARCH_SUGGESTIONS } from '../../utils/const';
import type {ILocalesTransformers, ITranslate} from '../../utils/locales';
import {LocalesConsumer} from '../../utils/locales';
import {Root, ToolbarContainer, StyledToolbar, SearchButton, SearchIconMobile} from './StyledSearchBar';

type Props = {
    classes: Object,
    location: Object,
    theme: Object,
    isMobile: boolean,
}

type State = {
    search: Object,
    searchAnchor: boolean,
}

class SearchBar extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            searchAnchor: false,
            search: {
                value: '',
                opened: false,
            },
        };
        this.search = this.search.bind(this);
    }

    toggleSearch() {
        const searchAnchor: boolean = !(this.state.searchAnchor);
        this.setState({ searchAnchor });
    }

    changeField(name: string): Function {
        return (event: string | Object) => {
            let value = '';
            if (typeof event === 'string') {
                value = event;
            } else {
                value = event.target.value;
            }
            this.setState((state: State): Object =>
                ({ [name]: merge(state[name], { value: value.replace(/[\s]/gm, '') }) }));
        };
    }

    search = () => {
        if (this.state.search.value) {
            this.stripUrlAndNavigate(this.state.search.value);
        }
    }

    stripUrlAndNavigate(url: string) {
        location.pathname = `/scorecard/${url
            .replace(/(https:\/\/|http:\/\/)/gi, '')
            .replace(/(www\.)/g, '')
            .replace(/(\/$)/g, '')
            .replace(/(\/|\?|#&).*/, '')
        }`;
    }

    render(): any {
        let { isMobile } = this.props;

        return (
            <LocalesConsumer>
                {({translate}: ILocalesTransformers): React.Node => (
                    <Root>
                        <ToolbarContainer>
                            <StyledToolbar disableGutters={true}>
                                { this.renderSearchInput(false, translate) }
                                <SearchButton onClick={this.search}>
                                    { !isMobile ? 'SEARCH' : <SearchIconMobile/> }
                                </SearchButton>
                            </StyledToolbar>
                        </ToolbarContainer>
                    </Root>
                )}
            </LocalesConsumer>
        );
    }
    renderSearchInput(isMobile: boolean, translate: ITranslate): Node {
        return (
            <Downshift
                id={isMobile ? 'input-with-icon-grid' : 'downshift-0-input'}
                onChange={(item: Object): void => this.stripUrlAndNavigate(item.value)}
                inputValue={this.state.search.value.replace(/[\s]/gm, '') || ''}
                onStateChange={(changes: Object, stateAndHelpers: Object) => {
                    stateAndHelpers.setHighlightedIndex(null);
                    if (changes.inputValue) {
                        this.changeField('search')(changes.inputValue, stateAndHelpers);
                    }
                }}
                itemToString={(item: Object): string => item && item.value || ''}
            >
                {this.renderSuggestions.bind(this, {isMobile, translate})}
            </Downshift>
        );
    }

    renderSuggestions(params: Object, options: Object): Node {
        const {isMobile, translate} = params;
        let { classes } = this.props;
        let {
            getInputProps,
            getItemProps,
            isOpen,
            inputValue,
            highlightedIndex,
        } = options;

        // Check if the user started typing one of common TLDs
        let suggestions = getSuggestions(inputValue);

        return (
            <div className={classes.search}>
                <ClickAwayListener onClickAway={
                    () => {
                        this.forceUpdate();
                    }
                }>
                    <TextField
                        placeholder={translate('pages.scorecard.error.search.placeholder')}
                        label={translate('general.search')}
                        autoFocus={true}
                        // value={this.state.search.value || ''}
                        // onChange={this.changeField('search')}
                        fullWidth={true}
                        id="search-input"
                        InputProps={{
                            'type': 'url',
                            'tabIndex': 0,
                            'onFocus': () => {
                                this.forceUpdate();
                            },
                            'onKeyPress': (event: Object) => {
                                if (event.key === 'Enter') {
                                    this.search();
                                }
                            },
                            'disableUnderline': isMobile ? false : true,
                            'startAdornment': isMobile ? null : (
                                <InputAdornment
                                    className={classes.searchAdornment}
                                    position="start"
                                >
                                    <Search className={classes.searchAdornmentSVG} color="disabled" />
                                </InputAdornment>
                            ),
                            'classes': {
                                root: isMobile ? classes.mobileSearchInput : classes.searchRoot,
                                input: isMobile ? null : classes.searchInput,
                            },
                            ...getInputProps(),
                        }}
                        InputLabelProps={{
                            'shrink': true,
                            'className': classes.searchFormLabel
                        }}
                    />
                </ClickAwayListener>
                {isOpen && inputValue ? (
                    <Paper className={classes.paper} square>
                        {suggestions
                            .map((item: string, index: number): Node => {
                                    const isHighlighted = highlightedIndex === index;
                                    return (
                                        <MenuItem
                                            {...getItemProps({
                                                key: item,
                                                index,
                                                item: { value: item },
                                            })}
                                            key={item}
                                            selected={isHighlighted}
                                            component="div"
                                            style={{
                                                fontWeight: isHighlighted ? 500 : 400,
                                            }}
                                            onClick={(): void => this.stripUrlAndNavigate(item)}
                                        >
                                            {item}
                                        </MenuItem>
                                    );
                                }
                            )
                        }
                    </Paper>
                ) : null}
            </div>
        );
    }
}

function getAllPossibleTldPrefixes(inputValue: string): Array<string> {
    let possibleTldPrefixes = [];

    // We use length-1 since we don't want to return '.' as TLD prefix
    // (we require at least 1 letter of the TLD)
    for (let i=0; i<inputValue.length-1; i++) {
        if (inputValue[i] === '.') {
            possibleTldPrefixes.push(inputValue.substring(i));
        }
    }

    return possibleTldPrefixes;
}

function applyTldOnInputValue(inputValue: string,
                              tldPrefix: string,
                              tldToApply: string): string {
    return inputValue.substring(0, inputValue.length - tldPrefix.length).concat(tldToApply);
}

function getSuggestions(inputValue: string): Array<string> {
    // normalize user input value
    inputValue = inputValue.toLowerCase();

    // If input value looks like an IPv4 pattern, we should
    // not return any suggestions
    if (REGEX.IPV4_SUBSTRING.test(inputValue)) {
        return [];
    }

    // Calculate the possible TLDs the user typed
    let possibleTldPrefixes = getAllPossibleTldPrefixes(inputValue);

    // Find all the TLDs that could match the input value and add them to the suggestion list
    // For example: let's say the user typed "www.something.co", we will add ".com", ".co.il, etc."
    // If the user typed "www.something.co.". we will add ".co.il, .co.uk, etc."
    // We also save the position of the first TLD match as this is the longest TLD-prefix possible
    // We do that because we want to show around 4-5 suggestions. If we don't find enough TLDs, we
    // would like to suggest the domain under a different TLD. For example: if the user typed
    // "www.example.r", we would like to show "www.example.ru" but also "www.example.com" and not "www.example.r.com"
    let suggestions = [];
    let topTlds = TOP_TLDS.slice(0);

    // Set the initial value of the longestTldPrefix.
    // If the inputValue ends with a '.', we use '.' to avoid from suggestions like "example..com"
    let longestTldPrefix = inputValue.endsWith('.') ? '.' : '';

    for (let i=0; i<possibleTldPrefixes.length && suggestions.length < MAX_SEARCH_SUGGESTIONS; i++) {
        for (let j=0; j<topTlds.length && suggestions.length < MAX_SEARCH_SUGGESTIONS; j++) {
            if (topTlds[j].startsWith(possibleTldPrefixes[i])) {
                // Add the suggestion
                const suggestion = applyTldOnInputValue(inputValue, possibleTldPrefixes[i], topTlds[j]);
                if (suggestions.indexOf(suggestion) === -1) { // remove duplications
                    suggestions.push(suggestion);
                }

                // Remove this TLD in order not to recommend it twice
                topTlds.splice(j, 1);

                // Update the longestTldPrefix variable if needed
                if (longestTldPrefix.length < possibleTldPrefixes[i].length) {
                    longestTldPrefix = possibleTldPrefixes[i];
                }
            }
        }
    }

    // Complete to number of suggestions
    for (let i=0; i<topTlds.length && suggestions.length < MAX_SEARCH_SUGGESTIONS; i++) {
        suggestions.push(applyTldOnInputValue(inputValue, longestTldPrefix, topTlds[i]));
    }

    return suggestions;
}


export default withRouter(withDialogRouter(withStyles(styles, { withTheme: true })(withSnackbar(SearchBar))));
